import React, { useCallback, useEffect, useState } from 'react';
import { GridApi, GridReadyEvent } from 'ag-grid-community';
import TextFloatingFilter from 'ui/AgGridReact/TextFloatingFilter';
import { PreviewColumnT, PreviewData } from 'routes/Cellar/types';
import Paper from '@mui/material/Paper';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import { styled } from '@mui/material/styles';
import PreviewHeader from '../PreviewHeader/PreviewHeader';
import { getPreviewColDef } from './helpers/colDefs';
import { useGetColumnType } from './hooks/columnType';

type Props = {
    file: PreviewData;
    columns: PreviewColumnT[];
    setColumns: React.Dispatch<PreviewColumnT[]>;
    isNewTable: boolean;
    tableColumns: ResponseColumnT[] | undefined;
};

const StyPaper = styled(Paper, {
    name: 'StyPaper',
})({
    marginTop: '1rem',
    maxWidth: 'calc(100vw - 400px)',
    flexGrow: 1,
    display: 'flex',
});

const StyPreviewContainer = styled('div', {
    name: 'StyPreviewContainer',
})({
    padding: '1.5rem',
    flexGrow: 1,
    display: 'flex',
    overflowY: 'auto',
    overflowX: 'auto',
    tbody: {
        tr: {
            '&:nth-of-type(odd)': {
                backgroundColor: '#f2f2f2',
            },
            '&:hover': {
                backgroundColor: '#1e3869',
            },
        },
    },
});

const StyAgContainer = styled('div', {
    name: 'StyAgContainer',
})({
    width: '100%',
    flexGrow: 1,
    '&& .ag-root-wrapper': {
        borderBottomLeftRadius: '4px',
        borderBottomRightRadius: '4px',
    },
});

const PreviewTable = ({
    file,
    columns,
    setColumns,
    isNewTable,
    tableColumns,
}: Props): React.ReactElement => {
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
        setGridApi(params.api);
    };

    const { rows, nbRows } = file;
    const getColumnType = useGetColumnType(isNewTable, tableColumns);

    const columnsDef = useCallback(() => {
        const colDefs = columns.map((column) => {
            const type = getColumnType(column);
            return getPreviewColDef(
                column,
                // TODO StrictNullCheck : Try to remove "!"
                type!,
                nbRows,
                setColumns,
                isNewTable,
            );
        });
        if (gridApi) {
            gridApi.setGridOption('columnDefs', colDefs);
        }
    }, [columns, getColumnType, gridApi, nbRows, setColumns]);

    useEffect(() => {
        if (gridApi) {
            gridApi.refreshCells({ force: true });
        }
    }, [gridApi, columns]);

    return (
        <StyPreviewContainer>
            <StyPaper>
                <StyAgContainer className="ag-theme-material">
                    <AgGridReact
                        alwaysShowHorizontalScroll
                        alwaysShowVerticalScroll
                        rowHeight={20}
                        headerHeight={60}
                        rowData={rows}
                        onGridReady={onGridReady}
                        components={{
                            textFloatingFilter: TextFloatingFilter,
                            agColumnHeader: PreviewHeader,
                        }}
                        // @ts-expect-error Found when typing AgGrid. columnsDef should return columns
                        columnDefs={columnsDef()}
                    />
                </StyAgContainer>
            </StyPaper>
        </StyPreviewContainer>
    );
};

export default React.memo(PreviewTable);
